/**
 * arrivalDate is a UTC-3 wall-clock value (timestamp without time zone).
 * Keep it as digit strings — never Date#toISOString() / Date.UTC round-trips.
 */

const WALL_CLOCK_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/;

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

/** YYYY-MM-DDTHH:mm for datetime-local, digits only (ignores Z / offsets). */
export function formatWallClockDateTimeForInput(value: string | Date | null | undefined): string {
  if (value == null || value === '') return '';

  if (typeof value === 'string') {
    const match = value.trim().match(WALL_CLOCK_RE);
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}`;
    }
  }

  if (value instanceof Date && !isNaN(value.getTime())) {
    // Local parts: Unspecified API strings become local Dates in JS.
    return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}T${pad2(value.getHours())}:${pad2(value.getMinutes())}`;
  }

  return '';
}

/**
 * datetime-local → plain string for JSON (no Date, so no UTC shift on serialize).
 */
export function parseWallClockDateTimeInput(value: string): string | undefined {
  if (!value) return undefined;
  const match = value.trim().match(WALL_CLOCK_RE);
  if (!match) return undefined;
  const [, y, mo, d, h, mi] = match;
  return `${y}-${mo}-${d}T${h}:${mi}:00`;
}

/** Normalize API payload to the same plain wall-clock string. */
export function parseWallClockDateTimeFromApi(value: string | Date | null | undefined): string | undefined {
  const input = formatWallClockDateTimeForInput(value);
  return input ? `${input}:00` : undefined;
}

/** dd/MM/yyyy HH:mm from wall-clock digits. */
export function formatWallClockDateTime(value: string | Date | null | undefined): string {
  const formatted = formatWallClockDateTimeForInput(value);
  if (!formatted) return '';
  const [datePart, timePart] = formatted.split('T');
  const [y, mo, d] = datePart.split('-');
  return `${d}/${mo}/${y} ${timePart}`;
}
